import {CELL_COLLAPSED, CELL_KEY_SEP, CELL_WILDCARD, RateFactor, PropertyCharacteristicGroup} from "./RateConfig";

/**
 * Auto-derive a stable id for a {@link RateFactor}.
 *
 * The id is keyed by the factor's SIGNATURE (kind + property + groups
 * structure), NOT by its values (cell amounts / infeasible flags).
 * That means editing a cell's amount on either the SP base or the
 * Org override produces the same id on both sides, so
 * {@link RateConfigEvaluator.mergeOverride} keeps merging by id
 * correctly.
 *
 * Algorithm (mirrors `docs/contracts/shared/RateConfig.md`):
 *  1. Build a canonical JSON object: `{kind, property: property ?? null,
 *     groups: groups.map(g => ({characteristics: [...g.characteristics],
 *     cellKeys: Object.keys(g.cells).sort()})).sort(by characteristics
 *     joined with '|').}`
 *  2. JSON.stringify that with stable key order (already provided by
 *     constructing the object literal in fixed order).
 *  3. 64-bit FNV-1a → 16 lowercase hex chars.
 *  4. Prefix with `<kind>-`.
 *
 * The 64-bit FNV-1a is implemented as two 32-bit lanes to stay safe
 * inside JavaScript's 53-bit number precision and avoid `BigInt`
 * (which is widely supported but slower and not strictly needed for
 * a 64-bit hash). The output is identical, byte-for-byte, to a
 * straightforward big-int FNV-1a — verified against test vectors.
 */
export function computeFactorId(factor: RateFactor): string {
  const canonical = canonicalSignature(factor);
  const json = JSON.stringify(canonical);
  const hex = fnv1a64Hex(json);
  return `${factor.kind}-${hex}`;
}

/** Build the canonical signature object used as input to the hash. */
export function canonicalSignature(factor: RateFactor): any {
  const groupSigs = (factor.groups || []).map(canonicalGroupSignature);
  // Sort groups by characteristics joined with a comma. The validator
  // rejects two groups with the same SET of characteristics, and within
  // a group the order is positional (different orders produce different
  // cell-key tuples and therefore different `cellKeys` sets), so the
  // joined-with-comma string is unique across groups within a factor.
  // No secondary tiebreaker is reachable; if it ever becomes reachable
  // (e.g. validator rules relax), fall through to authored order
  // (`Array.prototype.sort` is stable in modern runtimes).
  groupSigs.sort((a, b) => {
    const aj = a.characteristics.join(',');
    const bj = b.characteristics.join(',');
    return aj < bj ? -1 : aj > bj ? 1 : 0;
  });
  return {
    kind: factor.kind,
    property: factor.property == null ? null : factor.property,
    groups: groupSigs
  };
}

function canonicalGroupSignature(g: PropertyCharacteristicGroup): { characteristics: string[]; cellKeys: string[] } {
  // Normalize CELL_COLLAPSED ('-') tokens to CELL_WILDCARD ('*') before
  // hashing. The two are semantically equivalent for matching; the
  // distinction is purely editor presentation. Treating them as
  // equivalent here keeps the auto-derived factor `id` stable as the
  // user toggles expand/collapse on an axis position, which keeps
  // `mergeOverride()` keying intact across edits. The validator
  // separately forbids authoring both `-` and `*` for the same
  // canonical combo within one group's cells.
  const rawKeys = Object.keys(g.cells || {});
  const normalized = new Set<string>();
  for (const k of rawKeys) {
    const tokens = k.split(CELL_KEY_SEP);
    for (let i = 0; i < tokens.length; i++) {
      if (tokens[i] === CELL_COLLAPSED) { tokens[i] = CELL_WILDCARD; }
    }
    normalized.add(tokens.join(CELL_KEY_SEP));
  }
  return {
    characteristics: [...(g.characteristics || [])],
    cellKeys: Array.from(normalized).sort()
  };
}

// ── 64-bit FNV-1a, JS-safe via dual 32-bit lanes ─────────────────────

const FNV_OFFSET_HI = 0xcbf29ce4 >>> 0;
const FNV_OFFSET_LO = 0x84222325 >>> 0;
const FNV_PRIME_HI  = 0x00000100 >>> 0;
const FNV_PRIME_LO  = 0x000001b3 >>> 0;

/**
 * 64-bit FNV-1a hash of a UTF-8-encoded string. Returns 16 lowercase
 * hex characters. Identical output to a BigInt or Node-Buffer
 * implementation; verified against the standard FNV-1a 64-bit test
 * vectors:
 *   ""        → cbf29ce484222325
 *   "a"       → af63dc4c8601ec8c
 *   "foobar"  → 85944171f73967e8
 */
export function fnv1a64Hex(input: string): string {
  let hi = FNV_OFFSET_HI;
  let lo = FNV_OFFSET_LO;
  // Iterate UTF-8 bytes of the input. We do the encoding inline rather
  // than via TextEncoder so this works in older runtimes / SSR.
  for (let i = 0; i < input.length; i++) {
    const cp = input.charCodeAt(i);
    if (cp < 0x80) {
      [hi, lo] = mixByte(hi, lo, cp);
    } else if (cp < 0x800) {
      [hi, lo] = mixByte(hi, lo, 0xc0 | (cp >> 6));
      [hi, lo] = mixByte(hi, lo, 0x80 | (cp & 0x3f));
    } else if (cp >= 0xd800 && cp <= 0xdbff && i + 1 < input.length) {
      // Surrogate pair → 4-byte encoding.
      const lowSurr = input.charCodeAt(i + 1);
      i++;
      const code = 0x10000 + (((cp - 0xd800) << 10) | (lowSurr - 0xdc00));
      [hi, lo] = mixByte(hi, lo, 0xf0 | (code >> 18));
      [hi, lo] = mixByte(hi, lo, 0x80 | ((code >> 12) & 0x3f));
      [hi, lo] = mixByte(hi, lo, 0x80 | ((code >> 6) & 0x3f));
      [hi, lo] = mixByte(hi, lo, 0x80 | (code & 0x3f));
    } else {
      [hi, lo] = mixByte(hi, lo, 0xe0 | (cp >> 12));
      [hi, lo] = mixByte(hi, lo, 0x80 | ((cp >> 6) & 0x3f));
      [hi, lo] = mixByte(hi, lo, 0x80 | (cp & 0x3f));
    }
  }
  return toHex32(hi) + toHex32(lo);
}

function mixByte(hi: number, lo: number, byte: number): [number, number] {
  // XOR low byte
  lo = (lo ^ byte) >>> 0;
  // Multiply by FNV prime as a 64-bit operation:
  //   (hi:lo) * (PRIME_HI:PRIME_LO) mod 2^64
  // Split into 16-bit lanes to keep all intermediate products under 2^53.
  const aLo = lo & 0xffff;
  const aMid = (lo >>> 16) & 0xffff;
  const aHi = hi & 0xffff;
  const aHiHi = (hi >>> 16) & 0xffff;

  const bLo = FNV_PRIME_LO & 0xffff;
  const bMid = (FNV_PRIME_LO >>> 16) & 0xffff;
  const bHi = FNV_PRIME_HI & 0xffff;
  const bHiHi = (FNV_PRIME_HI >>> 16) & 0xffff;

  // Compute the 16-bit-laned products that contribute to bits 0..63 mod 2^64.
  let r0 = aLo * bLo;
  let r1 = aLo * bMid + aMid * bLo;
  let r2 = aLo * bHi + aMid * bMid + aHi * bLo;
  let r3 = aLo * bHiHi + aMid * bHi + aHi * bMid + aHiHi * bLo;

  // Carry-propagate.
  r1 += r0 >>> 16; r0 &= 0xffff;
  r2 += r1 >>> 16; r1 &= 0xffff;
  r3 += r2 >>> 16; r2 &= 0xffff;
  r3 &= 0xffff;

  const newLo = ((r1 << 16) | r0) >>> 0;
  const newHi = ((r3 << 16) | r2) >>> 0;
  return [newHi, newLo];
}

function toHex32(n: number): string {
  const s = (n >>> 0).toString(16);
  return '00000000'.slice(s.length) + s;
}

